import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, BadgeCheck } from "lucide-react";

/**
 * Public Plyx featured-creators strip — rendered on the marketing landing
 * page (/plyx). Shows 3–5 creators (admin-pinned first, then auto-filled by
 * engagement). Replaces the previous behaviour where /plyx dropped visitors
 * straight into a single creator's feed video.
 *
 * Self-fetches from /api/plyx/featured-creators using a public axios
 * instance (no auth — works for signed-out visitors). Silently hides if
 * the endpoint is empty or the request fails.
 */
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const publicApi = axios.create({ baseURL: `${BACKEND_URL}/api`, withCredentials: false });

const FeaturedCreatorsStrip = ({ limit = 5 }) => {
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    publicApi
      .get("/plyx/featured-creators", { params: { limit } })
      .then((r) => {
        if (!cancelled) setCreators(r.data.creators || []);
      })
      .catch(() => { /* silent — strip hides */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [limit]);

  if (loading || creators.length === 0) return null;

  return (
    <div className="mt-12 max-w-5xl" data-testid="plyx-featured-creators-strip">
      <div className="flex items-center justify-between mb-5">
        <div className="text-[11px] uppercase tracking-[0.18em] text-white/55 font-semibold">
          Featured creators
        </div>
        <Link
          to="/plyx/leaderboard"
          data-testid="plyx-featured-see-all"
          className="text-[11px] uppercase tracking-[0.14em] text-white/55 hover:text-white inline-flex items-center gap-1"
        >
          See all <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-3 -mx-1 px-1">
        {creators.map((c) => (
          <Link
            key={c.plyx_user_id}
            to={`/plyx/u/${c.handle}`}
            data-testid={`plyx-featured-creator-${c.handle}`}
            className="shrink-0 w-44 sm:w-52 rounded-xl p-4 border border-white/10 bg-white/[0.04] hover:bg-white/[0.07] transition-colors"
          >
            <div className="flex items-center gap-3 mb-3">
              {c.avatar_url ? (
                <img
                  src={c.avatar_url}
                  alt=""
                  className="w-12 h-12 rounded-full object-cover border border-white/10"
                />
              ) : (
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center font-bold text-white text-lg"
                  style={{ background: "#E85D4E" }}
                >
                  {(c.display_name || c.handle || "?")[0].toUpperCase()}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-white truncate flex items-center gap-1">
                  @{c.handle}
                  {c.verified_creator && (
                    <BadgeCheck className="w-3.5 h-3.5 text-white/85" aria-label="Verified" />
                  )}
                </div>
                {c.country && (
                  <div className="text-[11px] text-white/55 truncate">{c.country}</div>
                )}
              </div>
            </div>
            {c.note ? (
              <p className="text-[12px] text-white/70 leading-snug line-clamp-2">{c.note}</p>
            ) : c.bio ? (
              <p className="text-[12px] text-white/55 leading-snug line-clamp-2">{c.bio}</p>
            ) : (
              <p className="text-[12px] text-white/45 leading-snug">
                {(c.video_count || 0)} videos · {(c.follower_count || 0).toLocaleString()} followers
              </p>
            )}
          </Link>
        ))}
      </div>
    </div>
  );
};

export default FeaturedCreatorsStrip;
